import React, { Fragment, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { Pencil } from 'lucide-react';
import { formatEuro, monthKey, plural } from '../format';
import { useAdminData } from '../store';
import { Avatar, ListState, MonthSwitcher, SearchInput, useFreshIds } from '../ui';
import DealForm from './DealForm';

const dayLabel = (iso) => {
  const day = dayjs(iso).locale('nl');
  const today = dayjs().startOf('day');
  if (day.isSame(today, 'day')) return `Vandaag, ${day.format('dddd D MMMM')}`;
  if (day.isSame(today.subtract(1, 'day'), 'day')) return `Gisteren, ${day.format('dddd D MMMM')}`;
  const text = day.format('dddd D MMMM');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const Deals = () => {
  const [month, setMonth] = useState(() => dayjs().startOf('month'));
  const [direction, setDirection] = useState('next');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);

  const { data, loading, error, reload } = useAdminData({
    deals: 'deals',
    users: 'users',
    products: 'products',
  });

  const rows = useMemo(() => {
    if (!data) return [];
    const userNames = new Map(data.users.map((user) => [user.user_id, user.name]));
    const productNames = new Map(data.products.map((product) => [product.product_id, product.name]));
    return data.deals
      .map((deal) => ({
        ...deal,
        seller: userNames.get(deal.user_id) || 'Onbekend',
        service: productNames.get(deal.product_id) || 'Onbekend',
      }))
      .sort(
        (a, b) =>
          String(b.created_at || '').localeCompare(String(a.created_at || '')) ||
          b.deal_id - a.deal_id,
      );
  }, [data]);

  const fresh = useFreshIds(rows, (row) => row.deal_id);

  const changeMonth = (next) => {
    setDirection(next.isBefore(month) ? 'prev' : 'next');
    setEditing(null);
    setMonth(next);
  };

  const shown = useMemo(() => {
    const key = month.format('YYYY-MM');
    const needle = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        monthKey(row.created_at) === key &&
        (!needle || `${row.seller} ${row.service} ${row.business}`.toLowerCase().includes(needle)),
    );
  }, [rows, month, query]);

  const days = useMemo(() => {
    const groups = [];
    shown.forEach((row) => {
      const date = String(row.created_at || '').slice(0, 10);
      let group = groups[groups.length - 1];
      if (!group || group.date !== date) {
        group = { date, rows: [], total: 0 };
        groups.push(group);
      }
      group.rows.push(row);
      group.total += Number(row.price || 0);
    });
    return groups;
  }, [shown]);

  return (
    <>
      <DealForm onDone={() => {}} />

      <section className="adm-panel">
        <div className="adm-panel-head">
          <MonthSwitcher month={month} onChange={changeMonth} />
          {data && <span className="adm-count num">{plural(shown.length, 'deal', 'deals')}</span>}
          <SearchInput value={query} onChange={setQuery} placeholder="Zoek op bedrijf, verkoper of dienst" />
        </div>

        <ListState
          loading={loading && !data}
          error={error && !data}
          onRetry={reload}
          empty={days.length === 0 && (query ? 'Geen deals gevonden.' : 'Nog geen deals in deze maand.')}
        >
          <div key={month.format('YYYY-MM')} className={`adm-swap is-${direction}`}>
          {days.map((day) => (
            <div key={day.date} className="adm-day">
              <div className="adm-day-head">
                <span>{dayLabel(day.date)}</span>
                <span className="num">{formatEuro(day.total)}</span>
              </div>
              {day.rows.map((row) => {
                const open = editing === row.deal_id;
                return (
                <Fragment key={row.deal_id}>
                <button
                  type="button"
                  className={`adm-row${open ? ' is-open' : ''}${fresh.has(row.deal_id) ? ' is-fresh' : ''}`}
                  aria-expanded={open}
                  onClick={() => setEditing(open ? null : row.deal_id)}
                >
                  <Avatar name={row.seller} />
                  <span className="adm-row-main">
                    <b>{row.business || 'Onbekend bedrijf'}</b>
                    <small>{row.service}</small>
                  </span>
                  <span className="adm-row-seller">{row.seller}</span>
                  <span className={`adm-kind${Number(row.status) === 1 ? ' is-new' : ''}`}>
                    {Number(row.status) === 1 ? 'Nieuw' : 'Bestaand'}
                  </span>
                  <span className="adm-row-amount num">{formatEuro(row.price)}</span>
                  <Pencil size={15} className="adm-row-edit" aria-label="Bewerken" />
                </button>
                {open && (
                  <div className="adm-row-editor">
                    <DealForm deal={row} onDone={() => setEditing(null)} />
                  </div>
                )}
                </Fragment>
                );
              })}
            </div>
          ))}
          </div>
        </ListState>
      </section>
    </>
  );
};

export default Deals;
